/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment.js library for date and time formatting.
import _ from 'underscore' // Npm: Underscore.js library for utility functions.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { useQuery } from '@apollo/client' // Npm: Apollo client for GraphQL queries and mutations.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import RateUpsert from 'components/RateUpsert'
import RateDelete from 'components/RateDelete'
import RateDropDownOptions from 'components/RateDropDownOptions'


/*
 * GRAPHS
 */
import RateReadQuery from './__query__/index.rate.read.query'


/*
 * STYLES
 */
import { buttonStyle, tabStyles } from './index.style'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _rates = Object.React.App.enums.CUSTOMER_TYPE.enums.map(i => i.key)
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filter, setFilter] = React.useState({ 'items': [] })
  const _setFilterDebounced = React.useMemo(() => _.debounce(setFilter, 500), [])
  const [tabIndex, setTabIndex] = React.useState(0)

  const { 'isOpen': isRateCreateFormOpen, 'onOpen': onRateCreateFormOpen, 'onClose': onRateCreateFormClose } = useDisclosure()
  const { 'isOpen': isRateUpdateFormOpen, 'onOpen': onRateUpdateFormOpen, 'onClose': onRateUpdateFormClose } = useDisclosure()
  const { 'onOpen': onDeleteModalOpen, 'isOpen': isDeleteModalOpen, 'onClose': onDeleteModalClose } = useDisclosure()


  // Data assignment.
  const _isFilterApplied = filter?.items?.some(i => i.value != void 0 && i.value !== '')
  const _appliedFilter = _isFilterApplied ? filter : void 0


  const _QueryRateRead = useQuery(RateReadQuery, {
    'variables': {
      'type': _rates[tabIndex],
      'filter': _appliedFilter,
      'skip': paginationModel.page * paginationModel.pageSize,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data assignment.
  if (!_QueryRateRead.loading && 0 < _QueryRateRead.data?.RateRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryRateRead?.data?.RateRead?.length || !_.every(_QueryRateRead?.data?.RateRead, j => _successFlags.includes(j.status)) || _.some(_QueryRateRead?.data?.RateRead, j => 'RATE_PLAN_NOT_FOUND' === j.status)) _isFirstLoadCompleted.current = true

  // Define columns
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': true,
      'disableColumnMenu': true
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'disableColumnMenu': true,
      'headerAlign': 'center'
    },
    {
      'field': 'displayName',
      'headerName': 'Rate Name',
      'description': 'Name of the rate.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'type',
      'headerName': 'Rate Type',
      'description': 'Type of rate.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'disableColumnMenu': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 130
    },
    {
      'field': 'createdAt',
      'headerName': 'CreatedAt',
      'description': 'Creation date of rate.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120
    },
    {
      'field': 'updatedAt',
      'headerName': 'UpdatedAt',
      'description': 'Last update date of rate.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120
    },
    {
      'field': 'createdBy',
      'headerName': 'Created By',
      'description': 'User who created the rate.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'action',
      'headerName': 'Action',
      'description': 'Actions available for the rate.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      renderCell: ({ row }) => (
        <RateDropDownOptions
          type={row?.type}
          rateId={row?.id}
          onRateDelete={onDeleteModalOpen}
          onRateUpdate={onRateUpdateFormOpen}
        />
      )
    }
  ], [])

  // Define rows
  const rows = React.useMemo(() => _.compact(_QueryRateRead?.data?.RateRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    ...r,
    'id': r?.id || `rate-${index}-${Date.now()}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'displayName': r?.displayName || '-',
    'type': r?.type || '-',
    'createdAt': r?.createdAt ? Moment(r.createdAt).format('YYYY-MM-DD') : '-',
    'updatedAt': r?.updatedAt ? Moment(r.updatedAt).format('YYYY-MM-DD') : '-',
    'createdBy': r?.Account?.displayName || '-'
  }) : null)), [_QueryRateRead?.data?.RateRead, paginationModel.page, paginationModel.pageSize])


  // Return component.
  return (
    <>
      <Flex className='rate base' height='auto' flexDirection='column'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          height='100%'
        >
          <Flex
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            bg='linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)'
            borderTopLeftRadius='20px'
            borderTopRadius='20px'
            p={_isCurrentViewMobile ? '12px' : '22px'}>
            <Tooltip label='Create new rate.' fontSize='sm'>
              <Button
                w='170px'
                onClick={onRateCreateFormOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                New Rate
              </Button>
            </Tooltip>
            <DownloadToExcel
              cellsData={
                0 === _QueryRateRead?.data?.RateRead?.length || !_.every(
                  _.pluck(_QueryRateRead?.data?.RateRead, 'status'),
                  i => _successFlags?.includes(i)
                ) ? [] : _QueryRateRead?.data?.RateRead?.map((item, __index) => ({
                  'S.No.': `${__index + (paginationModel.pageSize * paginationModel.page) + 1}.`,
                  'Rate Name': item?.displayName ?? '-',
                  'Rate Type': item?.type ?? '-',
                  'CreatedAt': Moment(item.createdAt).format('YYYY-MM-DD'),
                  'UpdatedAt': Moment(item.updatedAt).format('YYYY-MM-DD'),
                  'Created By': item?.Account?.displayName ?? '-'
                }))
              }
              headersData={[
                'S.No.',
                'Rate Name',
                'Rate Type',
                'CreatedAt',
                'UpdatedAt',
                'Created By'
              ].map(i => ({ 'key': i, 'label': i }))}
            />
          </Flex>
          <Tabs
            variant='enclosed'
            onChange={r => setTabIndex(r)}
            display='flex'
            flexDirection='column'
            align='center'
            w='100%'
            flex={1}
            isFitted
          >
            <TabList bg='linear-Gradient(73deg,#390EF6 0%, rgba(146, 68, 244, 1) 100%)' overflowX={_isCurrentViewMobile ? 'auto' : 'none'}>
              {_rates.map(u => (
                <Tab
                  color='white'
                  textDecoration={'line-through'}
                  _focus={{ 'boxShadow': 'none' }}
                  _selected={{ 'textDecoration': 'none' }}
                  style={{ ...tabStyles, 'border': 'none', 'borderRadius': 0 }}
                  p='1rem'
                  key={String.random(8)}
                >
                  {u}
                </Tab>
              ))}
            </TabList>
            <Flex
              height='calc(100vh - 300px)'
              minHeight='calc(100vh - 300px)'
              maxHeight='calc(100vh - 300px)'
              bg='white'
              flexDirection='column'
              border='2px solid #3A0FF6'
              borderTop='none'
              borderBottomRadius='15px'
              overflow='hidden'>
              {/* MUI DataGrid */}
              <DataGrid
                key={`${paginationModel.page}-${paginationModel.pageSize}-${tabIndex}`}
                rows={rows}
                columns={columns}
                rowHeight={58}
                rowCount={_.pluck(_QueryRateRead?.data?.RateRead, '_totalCount')?.[0] || 0}
                paginationMode='server'
                filterMode='server'
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                onFilterModelChange={_setFilterDebounced}
                pageSizeOptions={[8, 30, 50, 100]}
                slots={{
                  'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                  'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
                  'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
                }}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
                loading={_QueryRateRead?.loading}
                disableColumnMenu={false}
                sx={{
                  '& .MuiDataGrid-main': {
                    border: 'none',
                    borderRadius: 0
                  }
                }}
              />
            </Flex>
          </Tabs>
        </Flex>
        <Modal
          size='md'
          title='Rate Create'
          isOpen={isRateCreateFormOpen}
          onClose={onRateCreateFormClose}
        >
          <RateUpsert isCreateOnly={true} type={_rates[tabIndex]} />
        </Modal>
        <Modal
          size='md'
          title='Rate Update'
          isOpen={isRateUpdateFormOpen}
          onClose={onRateUpdateFormClose}
        >
          <RateUpsert type={_rates[tabIndex]} />
        </Modal>
        <Modal
          size='md'
          title='Rate Delete'
          isOpen={isDeleteModalOpen}
          onClose={onDeleteModalClose}>
          <RateDelete />
        </Modal>
      </Flex>
    </>
  )
}

/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORTS
 */
export default Index
